import React from "react";
import { motion } from "framer-motion";
import { MessageSquare, Bot, Workflow, Database, Mic, TrendingUp, Headphones, Sparkles, ArrowUpRight } from "lucide-react";

const services = [
  {
    icon: MessageSquare,
    title: "AI WhatsApp Automation",
    description: "Automate customer conversations, order tracking, and support on WhatsApp with intelligent AI agents that never sleep.",
    tag: "Most Popular",
    wide: true,
    color: "blue",
  },
  {
    icon: Bot,
    title: "AI Chatbots",
    description: "Deploy custom AI chatbots on your website, CRM, or app to handle leads and queries 24/7 with zero human involvement.",
    color: "violet",
  },
  {
    icon: Workflow,
    title: "Workflow Automation",
    description: "Eliminate repetitive tasks using n8n, Make, Zapier, and custom pipelines that connect your entire business stack.",
    color: "blue",
  },
  {
    icon: Database,
    title: "CRM Automation",
    description: "Sync leads, automate follow-ups, and enrich CRM data with AI-powered integrations in real time.",
    color: "violet",
  },
  {
    icon: Mic,
    title: "AI Voice Agents",
    description: "Build phone-based AI voice agents for inbound call handling, appointment booking, and FAQ resolution.",
    wide: true,
    color: "blue",
  },
  {
    icon: TrendingUp,
    title: "Lead Generation Systems",
    description: "Automated outbound campaigns, AI-powered lead scoring, and CRM enrichment pipelines to fill your pipeline.",
    color: "violet",
  },
  {
    icon: Headphones,
    title: "AI Customer Support",
    description: "Replace slow ticket systems with AI agents that resolve 80%+ of queries instantly, 24/7.",
    color: "blue",
  },
  {
    icon: Sparkles,
    title: "Custom AI Solutions",
    description: "Bespoke AI systems engineered around your specific business logic, workflows, and scale requirements.",
    wide: true,
    color: "violet",
  },
];

const colorMap = {
  blue: { icon: "bg-blue-500/10 text-blue-400", tag: "text-blue-400/80 border-blue-500/20 bg-blue-500/[0.07]", glow: "rgba(59,130,246,0.065)" },
  violet: { icon: "bg-violet-500/10 text-violet-400", tag: "text-violet-400/80 border-violet-500/20 bg-violet-500/[0.07]", glow: "rgba(139,92,246,0.065)" },
};

const onMove = (e) => {
  const rect = e.currentTarget.getBoundingClientRect();
  e.currentTarget.style.setProperty("--mouse-x", `${e.clientX - rect.left}px`);
  e.currentTarget.style.setProperty("--mouse-y", `${e.clientY - rect.top}px`);
};

function ServiceCard({ service, index }) {
  const c = colorMap[service.color];
  const Icon = service.icon;
  return (
    <motion.div
      initial={{ opacity: 0, y: 22 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.55, delay: (index % 4) * 0.07, ease: [0.22, 1, 0.36, 1] }}
      onMouseMove={onMove}
      className={`spotlight-card gradient-border-card card-inner-top relative rounded-2xl p-6 bg-[#0E0E12] border border-white/[0.06] cursor-default transition-all duration-300 hover:-translate-y-1 hover:border-white/[0.1] group ${
        service.wide ? "md:col-span-2" : ""
      }`}
    >
      {/* Inner bg glow on hover */}
      <div
        className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
        style={{ background: `radial-gradient(circle at 20% 20%, ${c.glow} 0%, transparent 60%)` }}
      />

      {/* Top row */}
      <div className="flex items-start justify-between mb-5">
        <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${c.icon}`}>
          <Icon className="w-4.5 h-4.5" style={{ width: "18px", height: "18px" }} strokeWidth={1.5} />
        </div>
        <div className="flex items-center gap-2">
          {service.tag && (
            <span className={`text-[9.5px] font-mono tracking-wider uppercase px-2 py-0.5 rounded-full border ${c.tag}`}>
              {service.tag}
            </span>
          )}
          <ArrowUpRight className="w-3.5 h-3.5 text-white/15 group-hover:text-white/45 transition-colors" />
        </div>
      </div>

      <h3 className="text-[14.5px] font-semibold text-white/90 mb-2 tracking-[-0.02em]">{service.title}</h3>
      <p className="text-[13px] text-white/38 leading-[1.65]">{service.description}</p>
    </motion.div>
  );
}

export default function Services() {
  return (
    <section id="services" data-testid="services-section" className="py-24 md:py-36 relative">
      <div className="section-divider" />

      {/* Section glow */}
      <div className="absolute top-0 right-0 w-[600px] h-[400px] pointer-events-none" style={{ background: "radial-gradient(ellipse at 100% 0%, rgba(59,130,246,0.05) 0%, transparent 60%)" }} />

      <div className="max-w-7xl mx-auto px-6 md:px-10 pt-12">
        <div className="max-w-xl mb-14">
          <motion.p initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} className="overline-badge mb-4">
            Services
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 16 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="text-[2.2rem] sm:text-[2.8rem] lg:text-[3.4rem] font-black tracking-[-0.04em] text-white leading-[1.0] mb-4"
          >
            Everything you need to{" "}
            <span className="gradient-text-static">automate.</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-[15px] text-white/40 leading-relaxed"
          >
            From WhatsApp workflows to custom AI agents — we build systems tailored precisely to your business.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {services.map((s, i) => <ServiceCard key={s.title} service={s} index={i} />)}
        </div>
      </div>
    </section>
  );
}
